import React, {
  useCallback, useMemo, useRef, useState, useEffect,
} from 'react';
import { isEmpty, map, pickBy } from 'lodash';
import { useHistory } from 'react-router-dom';

import Box from '@material-ui/core/Box';

import helper from '@/helper';
import config from '@/configs/config';
import firebase from '@/services/firebase';
import useFirebaseDatabaseQuery from '@/hooks/useFirebaseDatabaseQuery';
import useUpdateQueryParams from '@/hooks/useUpdateQueryParams';

import Carousel from '@/components/Carousel';
import Header from '@/containers/Layout/Header';

import ProgressBackdrop from '@/components/ProgressBackdrop';
import { LazyLoadingComponent } from '@/../LazyLoading';
import { useMediaQuery, withStyles } from '@material-ui/core';
import PreOrderCard from './PreOrderCard';
import Slider, { SliderItem, StyledLink } from './Slider';
import { useActivityContext } from '../../../../../activity/contexts/useActivityContext';
import ActivityModal from './ActivityModal';
import { useGlobalStoreContext } from '../../../../../contexts/globalStoreContext';

const TAB_MAP = {
  HOT_PRE_ORDER: 'HOT_PRE_ORDER',
  CLOSED_PRE_ORDER: 'CLOSED_PRE_ORDER',
};

const Row = withStyles((theme) => ({ root: { '&+&': { marginTop: theme.spacing(3) } } }))(Box);

const List = () => {
  const isSM = useMediaQuery((theme) => theme.breakpoints.up('sm'));
  const chunk = isSM ? 4 : 2;
  const [{ activeNav }, getUpdatedQueryParams] = useUpdateQueryParams();
  const history = useHistory();
  const bodyRef = useRef(undefined);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [activityModalData, setActivityModalData] = useState(undefined);
  const { activities } = useActivityContext();
  const {
    activityModalHasBeenShowed,
    setActivityModalHasBeenShowed,
  } = useGlobalStoreContext();

  React.useEffect(() => {
    if (!activeNav) {
      history.replace(getUpdatedQueryParams({ activeNav: TAB_MAP.HOT_PRE_ORDER }));
    }
  }, [activeNav, getUpdatedQueryParams, history]);

  const dataRef = firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/preOrderEvents`);
  const { isLoading, data: preOrders } = useFirebaseDatabaseQuery(dataRef);

  const { data: adImageData } = useFirebaseDatabaseQuery(
    firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/ads`)
  );

  const { data: tagsData, isLoading: isTagsDataLoading } = useFirebaseDatabaseQuery(
    firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/tags`)
  );

  const preOrdersWithoutHidden = useMemo(() => pickBy(preOrders || {}, (preOrder) => {
    const { isHiddenInWebsite } = preOrder || {};

    return !isHiddenInWebsite;
  }), [preOrders]);

  useEffect(() => {
    if (!isEmpty(activities) && !activityModalHasBeenShowed) {
      const appropriateActivityToShow = activities.find((activity) => {
        const { popupImageUrl, activityUrl } = activity;

        return popupImageUrl && activityUrl;
      });

      if (appropriateActivityToShow) {
        const { popupImageUrl, activityUrl } = appropriateActivityToShow || {};

        setActivityModalData({
          imageUrl: `${config.RESOURCE_URI}${popupImageUrl}`,
          ...(activityUrl && { urlToGo: `/activity/${activityUrl}` }),
        });
        setIsActivityModalOpen(true);
      }
    }
  }, [activities, activityModalHasBeenShowed, setIsActivityModalOpen]);

  const adImages = React.useMemo(() => {
    if (!adImageData) return [];
    if (!adImageData.default) return [];
    if (typeof adImageData.default !== 'string') return [];
    return [
      ...map(pickBy(activities || {}, (activity) => {
        const { bannerImageUrl } = activity || {};
        return bannerImageUrl;
      }), (activity) => {
        const { bannerImageUrl, activityUrl } = activity || {};
        return {
          imageUrl: `${config.RESOURCE_URI}${bannerImageUrl}`,
          ...(activityUrl && { urlToGo: `/activity/${activityUrl}` }),
        };
      }),
      ...adImageData.default.split(',').map((image) => ({
        imageUrl: `${config.RESOURCE_URI}${image}`,
        urlToGo: undefined,
      })),
    ];
  }, [adImageData, activities]);

  const activeCategory = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const [_page, category] = (activeNav || '').split('-');

    if (isTagsDataLoading) return '';
    if (tagsData.indexOf(category) < 0) return '';

    return category || '';
  }, [activeNav, tagsData, isTagsDataLoading]);

  const data = useMemo(() => {
    const result = {};

    if (isLoading) return result;

    if (activeCategory) {
      return Object
        .entries(preOrdersWithoutHidden || {})
        .map(([preOrderId, preOrder]) => {
          const { tag: preOrderTagsString, registerEndTime } = preOrder;

          if (!registerEndTime || registerEndTime <= new Date().getTime()) return false;
          if (preOrderTagsString.indexOf(activeCategory) < 0) return false;

          return {
            ...preOrder,
            basicSalePrice: helper.getBasePrice(preOrder),
            id: preOrderId,
          };
        })
        .filter((i) => i)
        .sort((a, b) => {
          const t1 = a?.registerEndTime || 0;
          const t2 = b?.registerEndTime || 0;
          return t1 - t2;
        })
        .sort((
          { rank: aRank = 0 },
          { rank: bRank = 0 }
        ) => parseInt(bRank, 10) - parseInt(aRank, 10));
    }

    (tagsData || '')
      .split(',')
      .filter((i) => i)
      .forEach((tag) => {
        Object.entries(preOrdersWithoutHidden || {}).forEach(([preOrderId, preOrder]) => {
          const { tag: preOrderTagsString, registerEndTime } = preOrder;

          if (!registerEndTime || registerEndTime <= new Date().getTime()) return;

          if (preOrderTagsString.indexOf(tag) > -1) {
            if (!result[tag]) result[tag] = [];

            const categoryPreOrders = result[tag];

            categoryPreOrders.push({
              ...preOrder,
              basicSalePrice: helper.getBasePrice(preOrder),
              id: preOrderId,
            });
          }
        });
      });

    // sort
    Object.entries(result).forEach(([category, categoryPreOrders]) => {
      result[category] = categoryPreOrders
        .sort((a, b) => {
          const t1 = a?.registerEndTime || 0;
          const t2 = b?.registerEndTime || 0;
          return t1 - t2;
        })
        .sort((
          { rank: aRank = 0 },
          { rank: bRank = 0 }
        ) => parseInt(bRank, 10) - parseInt(aRank, 10));
    });

    return result;
  }, [preOrdersWithoutHidden, tagsData, isLoading, activeCategory]);
  const onCategoryLinkClick = useCallback(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, []);

  return (
    <>
      <Header />
      <Box>
        <Carousel
          width={1}
          data={adImages}
        />
        <ActivityModal
          isOpen={isActivityModalOpen}
          data={activityModalData}
          onClose={() => {
            setIsActivityModalOpen(false);
            setActivityModalHasBeenShowed();
            setActivityModalData(undefined);
          }}
        />
        <Box mx={1} my={3} ref={bodyRef}>
          {activeCategory
            ? (
              <>
                <Box>
                  <StyledLink to="/pre-orders" onClick={onCategoryLinkClick}>
                    {'<'}
                    回到上一層列表
                  </StyledLink>
                  <Box fontSize={22} mt={2} fontWeight="bold">{activeCategory}</Box>
                  <Box mt={3} display="flex" flexDirection="column">
                    {[...Array(Math.ceil(data.length / chunk))]
                      .map((row, index) => data.slice(index * chunk, index * chunk + chunk))
                      .map((row, index) => (
                        <Row
                          display="flex"
                          width={1}
                          // eslint-disable-next-line react/no-array-index-key
                          key={`${activeCategory}-${index}`}
                        >
                          {
                          row.map((preOrder) => {
                            const { id } = preOrder || {};
                            return (
                              <SliderItem key={id} chunk={chunk}>
                                <LazyLoadingComponent>
                                  <PreOrderCard
                                    data={preOrder}
                                    preOrderId={id}
                                  />
                                </LazyLoadingComponent>
                              </SliderItem>
                            );
                          })
                        }
                        </Row>
                      ))}
                  </Box>
                </Box>
              </>
            ) : Object
              .entries(data || {})
              .map(([category, categoryPreOrders]) => (
                <Box minHeight={350} key={category}>
                  <Slider
                    chunk={chunk}
                    data={categoryPreOrders}
                    onCategoryLinkClick={onCategoryLinkClick}
                    title={category}
                    renderItem={(preOrder) => {
                      const { id } = preOrder || {};

                      return (
                        <PreOrderCard
                          data={preOrder}
                          preOrderId={id}
                        />
                      );
                    }}
                  />
                </Box>
              ))}
        </Box>
      </Box>
      {isLoading && <ProgressBackdrop isShow />}
    </>
  );
};

export default List;
